import React, { useCallback } from "react";
import { Col, Row } from "react-bootstrap";
import { useFormContext } from "react-hook-form";

import { Alert } from "components";
import { HookFormInputGroup } from "components/hookForms";
import { DaysToDeliveryFieldsNames } from "globalConstants";
import { MerchantRiskTabFieldData } from "types";
import { float2Normalizer } from "validators";
import {
	maxValue100,
	minValueDays,
	onlyMaxDigits3,
	onlyPercent,
	required,
	validateDaysToDelivery,
} from "validators/validation";

import styles from "./daysToDelivery.module.scss";

type DaysToDeliveryFieldsProps = {
	handleUpdateField: (fieldData: MerchantRiskTabFieldData) => void;
	handleBatchUpdateFields: (
		fieldsArray: { name: string; beName: string }[],
	) => void;
	isLoading: boolean;
	loadingFields: string[];
};

const {
	SameDay,
	OneThreeDays,
	FourSevenDays,
	EightFourteenDays,
	FifteenThirtyDays,
	ThirtyPlusDays,
	DaysOverThirty,
} = DaysToDeliveryFieldsNames;

export const DaysToDeliveryFieldsForMerchantRisk = ({
	handleUpdateField,
	handleBatchUpdateFields,
	isLoading,
	loadingFields,
}: DaysToDeliveryFieldsProps) => {
	const {
		register,
		formState: { errors },
	} = useFormContext();

	const checkIsLoading = useCallback(
		({ name }) => isLoading || loadingFields.includes(name),
		[isLoading, loadingFields],
	);

	const handleUpdateForm = useCallback(() => {
		handleBatchUpdateFields([
			SameDay,
			OneThreeDays,
			FourSevenDays,
			EightFourteenDays,
			FifteenThirtyDays,
			ThirtyPlusDays,
		]);
	}, [handleBatchUpdateFields]);

	return (
		<>
			<Alert
				variant="notice"
				message={
					<span className="font-italic">
						Sum of the total percentages cannot be more than 100%
					</span>
				}
				className="mb-4"
			/>
			<Row>
				<Col md={6}>
					<HookFormInputGroup
						name={SameDay.name}
						register={register}
						label={`${SameDay.displayName}, %`}
						colInput={6}
						colLabel={4}
						showRequiredMark
						blurLoading={checkIsLoading(SameDay)}
						onBlur={handleUpdateForm}
						validator={{
							maxValue100,
							required,
							validateDaysToDelivery,
						}}
						className={styles.inputGroup}
						normalize={onlyPercent}
						normalizeOnBlur={float2Normalizer}
						autoComplete="off"
						placeholder="0.00"
						errors={errors}
					/>
				</Col>
				<Col md={6}>
					<HookFormInputGroup
						name={OneThreeDays.name}
						register={register}
						label={`${OneThreeDays.displayName}, %`}
						colInput={6}
						colLabel={4}
						showRequiredMark
						blurLoading={checkIsLoading(OneThreeDays)}
						onBlur={handleUpdateForm}
						validator={{
							maxValue100,
							required,
							validateDaysToDelivery,
						}}
						className={styles.inputGroup}
						normalize={onlyPercent}
						normalizeOnBlur={float2Normalizer}
						autoComplete="off"
						placeholder="0.00"
						errors={errors}
					/>
				</Col>
			</Row>
			<Row>
				<Col md={6}>
					<HookFormInputGroup
						name={FourSevenDays.name}
						register={register}
						label={`${FourSevenDays.displayName}, %`}
						colInput={6}
						colLabel={4}
						showRequiredMark
						blurLoading={checkIsLoading(FourSevenDays)}
						onBlur={handleUpdateForm}
						validator={{
							maxValue100,
							required,
							validateDaysToDelivery,
						}}
						className={styles.inputGroup}
						normalize={onlyPercent}
						normalizeOnBlur={float2Normalizer}
						autoComplete="off"
						placeholder="0.00"
						errors={errors}
					/>
				</Col>
				<Col md={6}>
					<HookFormInputGroup
						name={EightFourteenDays.name}
						register={register}
						label={`${EightFourteenDays.displayName}, %`}
						colInput={6}
						colLabel={4}
						showRequiredMark
						blurLoading={checkIsLoading(EightFourteenDays)}
						onBlur={handleUpdateForm}
						validator={{
							maxValue100,
							required,
							validateDaysToDelivery,
						}}
						normalize={onlyPercent}
						normalizeOnBlur={float2Normalizer}
						className={styles.inputGroup}
						autoComplete="off"
						placeholder="0.00"
						errors={errors}
					/>
				</Col>
			</Row>
			<Row>
				<Col md={6}>
					<HookFormInputGroup
						name={FifteenThirtyDays.name}
						register={register}
						label={`${FifteenThirtyDays.displayName}, %`}
						colInput={6}
						colLabel={4}
						showRequiredMark
						blurLoading={checkIsLoading(FifteenThirtyDays)}
						onBlur={handleUpdateForm}
						validator={{
							maxValue100,
							required,
							validateDaysToDelivery,
						}}
						className={styles.inputGroup}
						normalize={onlyPercent}
						normalizeOnBlur={float2Normalizer}
						autoComplete="off"
						placeholder="0.00"
						errors={errors}
					/>
				</Col>
				<Col md={6}>
					<HookFormInputGroup
						name={DaysOverThirty.name}
						register={register}
						label={`${DaysOverThirty.displayName}, %`}
						colInput={6}
						colLabel={4}
						showRequiredMark
						blurLoading={checkIsLoading(DaysOverThirty)}
						onBlur={e =>
							handleUpdateField([
								{
									name: DaysOverThirty.name,
									beName: DaysOverThirty.beName,
									value: e.target.value,
								},
							])
						}
						validator={{ required, minValueDays }}
						normalize={onlyMaxDigits3}
						className={styles.inputGroup}
						autoComplete="off"
						placeholder="0"
						errors={errors}
					/>
				</Col>
			</Row>
			<Row>
				<Col md={{ span: 6, offset: 6 }}>
					<HookFormInputGroup
						name={ThirtyPlusDays.name}
						register={register}
						emptyLabel
						colInput={6}
						colLabel={4}
						blurLoading={checkIsLoading(ThirtyPlusDays)}
						onBlur={handleUpdateForm}
						validator={{
							maxValue100,
							required,
							validateDaysToDelivery,
						}}
						normalize={onlyPercent}
						normalizeOnBlur={float2Normalizer}
						className={styles.inputGroup}
						autoComplete="off"
						placeholder="0.00"
						addon="%"
						errors={errors}
					/>
				</Col>
			</Row>
		</>
	);
};
